import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Notification } from 'components/CustomNotification';
import {
  ExperienceContainer,
  SubmitContainer,
  ExperienceList,
} from 'pages/addprofile/Profile.style';
import {
  Input,
  Button,
  BasicIconButton,
  BrandIconSpinner,
  Select,
} from 'components';
import {
  InsertExperience,
  updateExperienceList,
  deleteExperience,
  getExperienceByuserId,
  addExperience,
} from 'redux/features/ExperienceSlice';
import TextEditor from 'components/TextEditor';
import {
  getCountryList,
  getJobTypeList,
  getWorkTypeList,
} from 'redux/features/DropDownSlice';
import { MonthList, ProfileLimit, YearList } from 'assert/OptionList';
import AutoComplete from 'components/AutoComplete';
import CheckBox from 'components/CheckBox';
import moment from 'moment';

import { ReactComponent as Delete } from 'assert/images/More/delete.svg';
import { ReactComponent as EditFilled } from 'assert/images/Profile/edit.svg';
import { ReactComponent as BackArrow } from 'assert/images/Common/backArrow.svg';
import { ReactComponent as Exclamation } from 'assert/images/Common/exclamation.svg';
import BasicPopover from 'components/Popover';
import { JobAlertPopover } from 'pages/jobs/JobPosts.style';

const Experiences = ({
  experienceList,
  experiencesData,
  changeAction,
  changeTitle,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { getProfile } = useSelector((state) => state?.ProfileSlice);
  const { workTypeList, jobList, countryList } = useSelector(
    (state) => state?.DropdownReducer
  );

  const [expid, setExpid] = useState(null);
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enableSubmit, setEnableSubmit] = useState(null);
  const [isAlertPopoverOpen, setIsAlertPopoverOpen] = useState(false);
  const [experienceDetails, setExperienceDetails] = useState({
    title: '',
    companyName: '',
    employmentType: '',
    workType: '',
    location: '',
    startMonth: '',
    startYear: '',
    endMonth: '',
    endYear: '',
    currentlyWorking: false,
    description: '',
  });
  const [errMsg, setErrmsg] = useState({
    title: '',
    companyName: '',
    location: '',
    startMonth: '',
    startYear: '',
    endMonth: '',
    endYear: '',
  });

  useEffect(() => {
    jobList?.length === 0 && dispatch(getJobTypeList());
    workTypeList?.length === 0 && dispatch(getWorkTypeList());
    countryList?.length === 0 && dispatch(getCountryList());
  }, []);

  const resetFields = () => {
    Object.keys(experienceDetails).forEach((data) => {
      experienceDetails[data] = '';
    });
    Object.keys(errMsg).forEach((data) => {
      errMsg[data] = '';
    });
    setExperienceDetails((prevState) => ({
      ...prevState,
    }));
    setErrmsg((prevState) => ({
      ...prevState,
    }));
    setEdit(false);
    setEnableSubmit(null);
    setLoading(false);
    onClose(false, 'experience');
    // experienceList('', 'experience');
  };

  useEffect(() => {
    if (enableSubmit !== null) {
      const validation = Object.keys(errMsg).every((it) => errMsg[it] === '');
      if (validation) {
        setLoading(true);
        const payload = {
          title: experienceDetails.title,
          employmentType: experienceDetails.employmentType,
          companyName: experienceDetails.companyName,
          location: experienceDetails.location.label || "",
          startDate:
          experienceDetails.startMonth ? experienceDetails.startMonth + '/' + experienceDetails.startYear : "",
          endDate: (experienceDetails.currentlyWorking !== true && experienceDetails.endMonth) ? experienceDetails.endMonth + '/' + experienceDetails.endYear : "",
          currentlyWorking: Boolean(experienceDetails.currentlyWorking),
          workType: experienceDetails?.workType,
          description: experienceDetails.description,
          createdById: getProfile?.userId,
        };
        dispatch(
          experiencesData?.title === 'Edit'
            ? updateExperienceList(payload, expid)
            : InsertExperience(payload)
        )
          .then((res) => {
            dispatch(addExperience(res.data));
            dispatch(getExperienceByuserId());
            resetFields();
            if(res.data.message){
              Notification(
                'success',
                experiencesData?.title === 'Edit'
                  ? 'Updated Successfully'
                  : 'Added Successfully'
              );
            }
          })
          .catch(() => {
            resetFields();
            Notification('error', 'Something went wrong');
          });
      }
    }
  }, [enableSubmit]);

  const handleValidation = useCallback(() => {
    const validationParams = { ...errMsg };
    if (!experienceDetails.title?.trim()) validationParams.title = 'Title is required';
    else validationParams.title = '';

    if (!experienceDetails.companyName?.trim())
      validationParams.companyName = 'Company Name is required';
    else validationParams.companyName = '';

    if (!experienceDetails.startMonth || !experienceDetails.startYear)
      validationParams.startMonth = 'Start Date is required';
    else validationParams.startMonth = '';

    const expStartMonth =
      experienceDetails.startMonth < 10
        ? '0' + experienceDetails.startMonth
        : experienceDetails.startMonth;

    const expEndMonth =
      experienceDetails.endMonth < 10
        ? '0' + experienceDetails.endMonth
        : experienceDetails.endMonth;

    if (
      !experienceDetails.currentlyWorking &&
      (!experienceDetails.endMonth || !experienceDetails.endYear)
    )
      validationParams.endMonth = 'End Date is required';
    else if (
      !experienceDetails.currentlyWorking &&
      moment('01/' + expStartMonth + '/' + experienceDetails.startYear).isAfter(
        '01/' + expEndMonth + '/' + experienceDetails.endYear
      )
    ) {
      validationParams.endMonth = '';
      validationParams.startMonth =
        'Start Date should not greater than end date';
    } else {
      validationParams.endMonth = '';
      validationParams.startMonth = '';
    }

    setErrmsg(validationParams);
  }, [errMsg, experienceDetails]);

  const handleSubmit = useCallback(() => {
    handleValidation();
    setEnableSubmit(!enableSubmit);
  }, [enableSubmit, handleValidation]);

  const handleChange = useCallback(
    (data, name) => {
      setExperienceDetails((prevState) => ({
        ...prevState,
        [name]: data,
      }));
    },
    [experienceDetails]
  );

  const editExperience = (data) => {
    if (!data?.id) return null;
    experienceDetails.title = data.title;
    experienceDetails.companyName = data.companyName;
    experienceDetails.location = data.location;
    experienceDetails.workType = data.workType;
    experienceDetails.employmentType = data.employmentType;
    experienceDetails.startMonth = data.startDate?.split('/')?.[0];
    experienceDetails.startYear = data.startDate?.split('/')?.[1];
    experienceDetails.endMonth = data.endDate?.split('/')?.[0];
    experienceDetails.endYear = data.endDate?.split('/')?.[1];
    experienceDetails.currentlyWorking = data.currentlyWorking;
    experienceDetails.description = data.description;
    setEdit(true);
    setExpid(data.id);
    changeAction(false, 'experience');

    setExperienceDetails((prevState) => ({
      ...prevState,
    }));
  };

  const onDeleteExperience = (deleteId) => {
    dispatch(deleteExperience(deleteId));
    changeAction(true, 'experience');
    resetFields();
  };

  const experienceAdd = () => {
    setEdit(true);
    setExpid(null);
    changeAction(false, 'experience');
    changeTitle('Add', 'experience');
  };

  const backToEdit = () => {
    setEdit(false);
    changeAction(true, 'experience');
    changeTitle('Edit', 'experience');
  };

  const closeModal = () => {
    resetFields();
    onClose(false, 'experience');
  };

  return (
    <>
      <BrandIconSpinner loading={loading} />
      <ExperienceContainer>
        {experiencesData.action ? (
          <ExperienceList>
            {experiencesData?.details?.length > 0 &&
              experiencesData?.details?.map((data) => {
                return (
                  <div className="show-exp-list" key={data.id}>
                    <div>
                      <div className="exp-title">
                        <div className="role">{data.title}</div>
                        <div className="exp-date">
                          {data.startDate +
                            ' ' +
                            (data.endDate ? `- ${data?.endDate}` : '- Present')}
                        </div>
                      </div>
                      <div className="sub-data">
                        {data.companyName}
                        {data.location ? ', ' + data.location : ''}
                      </div>
                      <div className="sub-data-flex">
                        {
                          workTypeList.find(
                            (workTypeItem) =>
                              workTypeItem.value === Number(data.workType)
                          )?.label
                        }
                        {data.employmentType ? (
                          <>
                            <span> • </span>{' '}
                            <div>
                              {
                                jobList.find(
                                  (employmentItem) =>
                                    employmentItem.value ===
                                    Number(data.employmentType)
                                )?.label
                              }
                            </div>
                          </>
                        ) : (
                          ''
                        )}
                      </div>
                    </div>
                    <div className="icon-action">
                      <div
                        onClick={() => editExperience(data)}
                        className="edit-icon"
                      >
                        <BasicIconButton
                          icon={<EditFilled />}
                          ariaLabel={'edit-icon'}
                          size="small"
                        />
                      </div>
                      <BasicPopover
                        handlePopoverClick={() => setIsAlertPopoverOpen(true)}
                        isOpen={isAlertPopoverOpen}
                        action={
                          <div className="remove-icon">
                            <BasicIconButton
                              icon={<Delete />}
                              ariaLabel={'remove-icon'}
                              size="small"
                            />
                          </div>
                        }
                        content={
                          <JobAlertPopover className="job-alert-popover">
                            Are you sure you want to delete?
                            <div>
                              <Button
                                variant="text"
                                color="error"
                                label="Yes"
                                onClick={() => onDeleteExperience(data.id)}
                              />
                              <Button
                                variant="text"
                                label="No"
                                onClick={() => setIsAlertPopoverOpen(false)}
                              />
                            </div>
                          </JobAlertPopover>
                        }
                      />
                    </div>
                  </div>
                );
              })}
          </ExperienceList>
        ) : (
          <div className="profile-experience-container">
            <Input
              label={t('profile.ProfileTitle')}
              value={experienceDetails.title}
              onChange={(data) => handleChange(data, 'title')}
              errMsg={errMsg.title}
              required
            />
            <div className="field-wrap">
              <Input
                label={t('profile.ProfileCmyName')}
                value={experienceDetails.companyName}
                onChange={(data) => handleChange(data, 'companyName')}
                errMsg={errMsg.companyName}
                required
              />
              <AutoComplete
                Options={countryList}
                label={t('profile.ProfileLocation')}
                value={experienceDetails.location}
                onChange={(data) => handleChange(data, 'location')}
                errMsg={errMsg.location}
              />
            </div>
            <div className="field-wrap">
              <Select
                Options={workTypeList}
                label={t('Job.WorkType')}
                value={experienceDetails.workType}
                onChange={(data) => handleChange(data.target.value, 'workType')}
              />
              <Select
                Options={jobList}
                label={t('Job.EmploymentType')}
                value={experienceDetails.employmentType}
                onChange={(data) =>
                  handleChange(data.target.value, 'employmentType')
                }
              />
            </div>
            <div className="field-wrap">
              <div className="date-container">
                <div className="lebelView">
                  <label>
                    {t('profile.StartDate')}
                    <span className="requiredSymbol">*</span>
                  </label>
                </div>
                <div className="date-select-field">
                  <div>
                    <Select
                      Options={MonthList}
                      value={experienceDetails.startMonth}
                      onChange={(data) =>
                        handleChange(data.target.value, 'startMonth')
                      }
                    />
                    {errMsg.startMonth && (
                      <span className="errMsg">
                        <Exclamation />
                        {errMsg.startMonth}
                      </span>
                    )}
                  </div>
                  <Select
                    Options={YearList}
                    value={experienceDetails.startYear}
                    onChange={(data) =>
                      handleChange(data.target.value, 'startYear')
                    }
                  />
                </div>
              </div>
              {!experienceDetails.currentlyWorking && (
                <div className="date-container">
                  <div className="lebelView">
                    <label>
                      {t('profile.EndDate')}
                      <span className="requiredSymbol">*</span>
                    </label>
                  </div>
                  <div className="date-select-field">
                    <div>
                      <Select
                        Options={MonthList}
                        value={experienceDetails.endMonth}
                        onChange={(data) =>
                          handleChange(data.target.value, 'endMonth')
                        }
                      />
                      {errMsg.endMonth && (
                        <span className="errMsg">
                          <Exclamation />
                          {errMsg.endMonth}
                        </span>
                      )}
                    </div>
                    <Select
                      Options={YearList}
                      value={experienceDetails.endYear}
                      onChange={(data) =>
                        handleChange(data.target.value, 'endYear')
                      }
                    />
                  </div>
                </div>
              )}
            </div>
            <CheckBox
              label={t('profile.CurrentWork')}
              onChange={(data) => handleChange(data, 'currentlyWorking')}
              value={experienceDetails.currentlyWorking}
              size="small"
            />
            <div className="exp-inputs">
              <TextEditor
                onChange={(e) => handleChange(e, 'description')}
                defaultValue={experienceDetails.description}
                label={t('profile.ProfileDescription')}
                value={experienceDetails.description}
                maxLength={1500}
              />
            </div>
          </div>
        )}
      </ExperienceContainer>
      <SubmitContainer className="modal-footer">
        {ProfileLimit.maxLimitProfile >=
          Number(experiencesData?.details?.length || 0) && (
          <>
            {edit && (
              <Button
                label={t('Back')}
                customStyles={`add-new`}
                onClick={backToEdit}
                type="text"
                startIcon={<BackArrow />}
              />
            )}
            {experiencesData.action && (
              <Button
                label={t('Add New')}
                customStyles={`add-new`}
                onClick={experienceAdd}
                type="text"
              />
            )}
            {!experiencesData.action && (
              <Button
                label={t('HomePage.Save')}
                onClick={handleSubmit}
                type="text"
              />
            )}
          </>
        )}
        <Button label={t('profile.Close')} onClick={closeModal} type="text" />
      </SubmitContainer>
    </>
  );
};
export default Experiences;
